// Kapabilitetsregisteret: plasserbare sensorer/droner/observasjonsposter/
// personer-som-observatør — HVA finnes, HVOR, og HVILKET behov kan den dekke.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERMÅL 2026-08-19: «hvordan kan man melde behov i SKADE/GNÅ for å beskrive
// HVA problemet er og la operatøren få anbefalt HVILKE kapabiliteter som løser
// det». Se `vale/RFI_SENSORPLASSERING_LLM_PLAN_2026-08-19.md` §3.
//
// `KamerakalibreringSchema` (kamerakalibrering.ts) løste ETT sensortilfelle
// (fast kamera). Denne fila generaliserer mønsteret (posisjon + retning/FOV +
// rekkevidde) til en TYPE-UNION som dekker resten av det eieren faktisk ber
// om plassert i kartet — inkludert to typer som ikke er «klassiske» sensorer:
// en soldat med mobiltelefon (US Army-doktrinen «every soldier is a sensor»,
// se forskningsgrunnlaget i planen §2) og en fast kystoppsynspost med BÅDE
// radar (lang deteksjonsrekkevidde) OG et speilreflekskamera (kortere
// identifiseringsrekkevidde) — «detect-then-classify» i maritim overvåking.
//
// ⚠ TALLENE I `KAPABILITET_PROFIL` ER TYPISKE, IKKE MÅLTE. De er utgangspunkt
// en operatør justerer ved faktisk plassering (feltarbeid, som kamera-
// kalibrering), ikke en påstand om et bestemt produkt. Der forskningen
// (deep-research, kapabilitets-runden 2026-08-19) gir konkrete produkttall,
// er de sitert i kommentaren ved feltet — resten er ingeniør-anslag markert
// som sådan.
import { z } from 'zod';
import { haversineM } from './geo.js';
import { TrackOrgSchema } from './track.js';
import { RfiPriority } from './rfi.js';
// ── Type-union ───────────────────────────────────────────────────────────────
export const KapabilitetType = z.enum([
    'bakkeradar', // Ground Surveillance Radar — persondeteksjon typisk kortere enn kjøretøy.
    'kystradar', // Fast kystradarstasjon, ofte AIS-korrelert.
    'drone', // Mobil, EO/IR-nyttelast, kort utholdenhet.
    'eo-ir-kamera', // Fast/PTZ EO/IR — generaliserer KamerakalibreringSchema.
    'akustisk', // Rekkevidde uten line-of-sight-krav (lyd går rundt hjørner viewshed ikke fanger).
    'kystmeldepost', // Bemannet, øyeblikkelig meldetid, kort rekkevidde (visuell/kikkert).
    'kyp', // Kystoppsynspost: radar (deteksjon) + speilreflekskamera (identifisering) på SAMME post.
    'soldat-mobil', // Menneske + smarttelefon: svak, mobil, intermitterende — «every soldier is a sensor».
    'annet',
]);
export const KAPABILITET_TYPE_LABEL = Object.freeze({
    bakkeradar: 'Bakkeradar',
    kystradar: 'Kystradar',
    drone: 'Drone',
    'eo-ir-kamera': 'EO/IR-kamera',
    akustisk: 'Akustisk sensor',
    kystmeldepost: 'Kystmeldepost',
    kyp: 'Kystoppsynspost (radar + kamera)',
    'soldat-mobil': 'Soldat med mobiltelefon',
    annet: 'Annet',
});
// ── Skjema ───────────────────────────────────────────────────────────────────
export const KapabilitetSchema = z.object({
    id: z.string().max(64),
    type: KapabilitetType,
    navn: z.string().max(120).optional(),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    hoydeOverBakkenM: z.number().min(0).max(500).optional(),
    /** Retningsbestemt (radar-sektor/kamera-FOV) ELLER udefinert = allsidig
     * (rundstrålende radar, akustisk, kystmeldepost). */
    asimutDeg: z.number().min(0).max(360).optional(),
    fovDeg: z.number().positive().max(360).optional(),
    /** Primær deteksjonsrekkevidde (m). For `kyp`: radarens rekkevidde. */
    rekkeviddeM: z.number().positive().max(200_000),
    /** KUN for dual-sensor-typer (`kyp` foreløpig): kortere rekkevidde der
     * OPTISK IDENTIFISERING (ikke bare deteksjon) er realistisk — kamera med
     * teleobjektiv leser detaljer på en brøkdel av radarens deteksjonsavstand.
     * Udefinert på en ikke-dual type betyr «identifisering = deteksjon». */
    identifiseringsrekkeviddeM: z.number().positive().max(200_000).optional(),
    // Doktrinens kapabilitets-delkriterier (§2 funn 1 i planen) — det
    // scoringsalgoritmen i `anbefalKapabiliteter` leser.
    dagNatt: z.enum(['kun-dag', 'kun-natt', 'begge']).default('begge'),
    mobil: z.boolean().default(false),
    klarTilBrukMin: z.number().int().min(0).max(1440).default(0),
    /** Bemannet av et MENNESKE som del av selve sensoren (ikke bare en
     * operatør bak en fast sensor). Sant for `soldat-mobil` — signalerer lavere
     * utholdenhet/pålitelighet enn en fast/autonom sensor i `anbefalKapabiliteter`,
     * og at «rekkevidde» her betyr synsrekkevidde/kamerarekkevidde, ikke et
     * datablad-tall. */
    menneskebasert: z.boolean().default(false),
    merknad: z.string().max(1000).optional(),
    org: TrackOrgSchema.optional(),
    opprettetMs: z.number(),
});
/**
 * Typiske (IKKE målte) startverdier per type — utgangspunkt i
 * plasseringspanelet, som operatøren alltid kan overstyre. Rekkevidder for
 * `bakkeradar` speiler den vanlige todelingen i produktblad: kortere for
 * person, lengre for kjøretøy — her forenklet til ETT konservativt
 * (person-)tall siden skjemaet har ett `rekkeviddeM`-felt; se merknadsfeltet
 * for kjøretøytallet ved faktisk plassering.
 */
export const KAPABILITET_PROFIL = Object.freeze({
    // ⚠ INGEN VERIFISERT KILDE FOR PERSON/KJØRETØY-SKILLE. Egen `/deep-research`-
    // runde 2026-08-19 (kapabilitets-spesifikk) lette AKTIVT etter reelle
    // produkt-/doktrinetall (person ~5-10 km, kjøretøy ~15-30 km) — ingen
    // påstand med konkrete tall overlevde adversarial verifisering (kun en
    // generisk leverandørpåstand om at GSR detekterer/klassifiserer person OG
    // kjøretøy, uten tall). Dette 8 km-tallet er derfor et rent ingeniøranslag,
    // IKKE et sitert produktblad — juster ved faktisk plassering/anskaffelse.
    bakkeradar: { rekkeviddeM: 8_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 30, menneskebasert: false },
    kystradar: { rekkeviddeM: 40_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 15, menneskebasert: false },
    drone: { rekkeviddeM: 3_000, dagNatt: 'kun-dag', mobil: true, klarTilBrukMin: 20, menneskebasert: false },
    'eo-ir-kamera': { rekkeviddeM: 2_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 5, menneskebasert: false },
    akustisk: { rekkeviddeM: 1_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 10, menneskebasert: false },
    kystmeldepost: { rekkeviddeM: 5_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 0, menneskebasert: true },
    // «Detect-then-classify»-mønsteret ER verifisert presedens (samme
    // /deep-research-runde, funn 4): Indias Integrated Coastal Surveillance
    // System co-plasserer en kystradar (instrumentert rekkevidde 50 km, pålitelig
    // deteksjon av små ~1 m²-mål innenfor 20 km) med dag-/natt-/varmekamera for
    // identifisering — samme post, to sensor-modaliteter. `rekkeviddeM` her er
    // derfor satt til ICSS sitt PÅLITELIGE deteksjonstall (20 km), ikke det
    // optimistiske instrument-maks-tallet. `identifiseringsrekkeviddeM` (3 km)
    // er FORTSATT et ingeniøranslag — et konkret kamera-ID-rekkevidde-tall
    // (f.eks. en «~25 km EO/IR»-påstand) ble AKTIVT REFUTERT i samme runde.
    kyp: { rekkeviddeM: 20_000, identifiseringsrekkeviddeM: 3_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 5, menneskebasert: true },
    // Synsrekkevidde/kamerarekkevidde for en person, IKKE et sensor-datablad.
    // Ingen IR uten spesialutstyr → kun-dag. Kan flytte seg, men er ikke en
    // «mobil sensor» i doktrinens forstand (utholdenhet = ett menneske).
    // ⚠ Samme forskningsrunde bekreftet KONSEPTET «soldat som sensor» solid
    // (US Army ES2-doktrine, Ukrainas Delta-system som eksplisitt lister
    // «vetted bystanders» som COP-kilde, ATAK sin BFT-posisjonsdeling fra vanlig
    // COTS-telefon) — men INGEN kilde ga et tall for rekkevidde, posisjons-
    // nøyaktighet eller meldetid for et menneske+telefon. 1500 m er derfor et
    // ingeniøranslag basert på realistisk synsrekkevidde, ikke en sitert kilde.
    'soldat-mobil': { rekkeviddeM: 1_500, dagNatt: 'kun-dag', mobil: true, klarTilBrukMin: 0, menneskebasert: true },
    annet: { rekkeviddeM: 1_000, dagNatt: 'begge', mobil: false, klarTilBrukMin: 30, menneskebasert: false },
});
/** Nytt kapabilitetsobjekt med typens standardprofil, klar til å redigeres. */
export function nyKapabilitetMedProfil(type, posisjon, opprettetMs, overstyring = {}) {
    const p = KAPABILITET_PROFIL[type];
    return {
        type,
        lat: posisjon.lat,
        lon: posisjon.lon,
        rekkeviddeM: p.rekkeviddeM,
        identifiseringsrekkeviddeM: p.identifiseringsrekkeviddeM,
        dagNatt: p.dagNatt,
        mobil: p.mobil,
        klarTilBrukMin: p.klarTilBrukMin,
        menneskebasert: p.menneskebasert,
        opprettetMs,
        ...overstyring,
    };
}
/** Maks minutter til klar, per hastegrad, før «for treg»-poenget trekkes. */
const KLARTIL_TERSKEL_MIN = Object.freeze({
    flash: 15,
    immediate: 60,
    priority: 240,
    routine: 1440,
});
/**
 * Ranger kandidatkapabiliteter mot et behov. Deterministisk og testbar:
 * samme input gir alltid samme output og samme begrunnelse.
 *
 * `identifiseringsrekkeviddeM`-typer (`kyp`) vurderes på DETEKSJONS-
 * rekkevidden (`rekkeviddeM`) her — «dekker behovet» handler om å SE noe der,
 * ikke om å identifisere det. Identifiseringsrekkevidden er informasjon til
 * operatøren i forslagsteksten, ikke et eget scoringskriterium.
 */
export function anbefalKapabiliteter(behov, kandidater) {
    const forslag = kandidater.map((k) => {
        let score = 0;
        const begrunnelse = [];
        // 1) Dekker området? — geometri, ikke gjetning.
        if (behov.omraade) {
            const avstandM = haversineM({ lat: k.lat, lon: k.lon }, { lat: behov.omraade.lat, lon: behov.omraade.lon });
            const nårUt = avstandM <= k.rekkeviddeM + behov.omraade.radiusM;
            if (nårUt) {
                score += 40;
                begrunnelse.push(`Dekker behovsområdet (${Math.round(avstandM / 100) / 10} km unna, rekkevidde ${Math.round(k.rekkeviddeM / 100) / 10} km).`);
            }
            else if (k.mobil) {
                // Mobil kapabilitet dekker ikke NÅ, men kan flyttes — delvis poeng,
                // ærlig begrunnet som «kan flyttes», ikke «dekker».
                score += 15;
                begrunnelse.push(`Dekker IKKE i dag (${Math.round(avstandM / 100) / 10} km unna), men er mobil og kan flyttes nærmere.`);
            }
            else {
                begrunnelse.push(`Dekker ikke behovsområdet (${Math.round(avstandM / 100) / 10} km unna, rekkevidde ${Math.round(k.rekkeviddeM / 100) / 10} km) og er fast montert.`);
            }
        }
        else {
            // Ingen områdeangivelse — kan ikke vurdere dekning, ikke straffe for det.
            score += 20;
        }
        // 2) Dag/natt-match.
        if (behov.krevesDagNatt && behov.krevesDagNatt !== 'begge') {
            const passer = k.dagNatt === 'begge' || k.dagNatt === behov.krevesDagNatt;
            if (passer) {
                score += 20;
                begrunnelse.push(`Virker ${behov.krevesDagNatt === 'kun-dag' ? 'på dagtid' : 'på nattid'} som behovet krever.`);
            }
            else {
                begrunnelse.push(`Virker KUN ${k.dagNatt === 'kun-dag' ? 'på dagtid' : 'på nattid'} — behovet krever ${behov.krevesDagNatt === 'kun-dag' ? 'dagtid' : 'nattid'}.`);
            }
        }
        else {
            score += 10;
        }
        // 3) Tilgjengelighet — klar til bruk innen hastegradens frist.
        const terskel = KLARTIL_TERSKEL_MIN[behov.hastegrad ?? 'routine'];
        if (k.klarTilBrukMin <= terskel) {
            score += 20;
            begrunnelse.push(k.klarTilBrukMin === 0
                ? 'Allerede i drift/observerer nå.'
                : `Klar innen ${k.klarTilBrukMin} min — innenfor ${behov.hastegrad ?? 'routine'}-fristen på ${terskel} min.`);
        }
        else {
            begrunnelse.push(`Klargjøring tar ${k.klarTilBrukMin} min — over ${behov.hastegrad ?? 'routine'}-fristen på ${terskel} min.`);
        }
        // 4) Menneskebasert kapabilitet: lavere utholdenhet/pålitelighet enn en
        // fast/autonom sensor (§ soldat-mobil i planen) — ærlig trukket, ikke
        // diskvalifiserende (en soldat med mobil er ofte det ENESTE som finnes
        // et gitt sted, og er bedre enn ingenting).
        if (k.menneskebasert) {
            score -= 5;
            begrunnelse.push('Menneskebasert observasjon: lavere utholdenhet/pålitelighet enn en fast sensor, men kan svare umiddelbart der ingen fast sensor finnes.');
        }
        // 5) Identifisering, ikke bare deteksjon — informasjon, ikke scoring.
        if (k.identifiseringsrekkeviddeM !== undefined) {
            begrunnelse.push(`Kan IDENTIFISERE innenfor ${Math.round(k.identifiseringsrekkeviddeM / 100) / 10} km (utover det: deteksjon uten identifisering).`);
        }
        return { kapabilitet: k, score, begrunnelse };
    });
    return forslag.sort((a, b) => b.score - a.score);
}
// ── Bestilling: «jeg har et behov, be om en kapabilitet dit» ────────────────
//
// Eiermål 2026-08-19: «lag system for bestilling og plassering, både i GNÅ og
// SKADE». En `Bestilling` er FORESPØRSELEN (fra felt eller COP-kart) — en
// `Kapabilitet` er RESULTATET når/hvis bestillingen innfris. De to skjemaene
// er bevisst SEPARATE (samme prinsipp som RFI vs. CollectionTask): en avvist
// eller ubehandlet bestilling skal aldri late som den er en utplassert
// kapabilitet.
export const BestillingStatus = z.enum(['bestilt', 'godkjent', 'avvist', 'utplassert', 'kansellert']);
export const BESTILLING_STATUS_LABEL = Object.freeze({
    bestilt: 'Bestilt — venter på behandling',
    godkjent: 'Godkjent — venter på utplassering',
    avvist: 'Avvist',
    utplassert: 'Utplassert',
    kansellert: 'Kansellert',
});
export const BestillingSchema = z.object({
    id: z.string().max(64),
    /** Fritekst — HVA problemet/behovet er. Samme felt som `Behov.tekst` over,
     * lagret varig fordi en bestilling (i motsetning til en ad hoc-anbefaling)
     * skal kunne behandles av noen andre enn den som skrev den. */
    behovTekst: z.string().max(2000),
    omraade: z.object({
        lat: z.number().min(-90).max(90),
        lon: z.number().min(-180).max(180),
        radiusM: z.number().positive().max(500_000),
    }).optional(),
    /** Ønsket type, hvis bestilleren allerede vet det (f.eks. fra GNÅ: «meld meg
     * selv tilgjengelig som observatør» setter denne til `soldat-mobil`).
     * Udefinert = «vet ikke, be om en anbefaling» — samme distinksjon som at
     * `Kapabilitet` alltid vet sin type, men en bestilling ikke nødvendigvis gjør. */
    onsketType: KapabilitetType.optional(),
    hastegrad: RfiPriority.default('routine'),
    status: BestillingStatus.default('bestilt'),
    /** Matrix-id/kallesignal for den som bestiller (menig fra GNÅ, operatør i COP). */
    bestiltAv: z.string().max(128),
    createdMs: z.number(),
    behandletAv: z.string().max(128).optional(),
    behandletMs: z.number().optional(),
    /** Påkrevd disiplin ved avvisning — samme «ingen avslag uten grunn»-prinsipp
     * som `begrunnelse` i `anbefalKapabiliteter` og verifiseringsavslag i
     * personregisteret. Håndheves av `avvisBestilling`, ikke av selve skjemaet
     * (skjemaet tillater lagring av mellomtilstander ved parsing fra lager). */
    avvistGrunn: z.string().max(500).optional(),
    /** Satt når `status: 'utplassert'` — kobling til den faktiske `Kapabilitet`. */
    kapabilitetId: z.string().max(64).optional(),
    org: TrackOrgSchema.optional(),
});
/** Godkjenn en bestilling. Ren (ny kopi) — ingen sideeffekt på lager her. */
export function godkjennBestilling(b, av, atMs) {
    return { ...b, status: 'godkjent', behandletAv: av, behandletMs: atMs };
}
/** Avvis en bestilling. `grunn` er PÅKREVD i signaturen (ikke bare i skjemaet)
 * — samme disiplin som `begrunnelse` i `anbefalKapabiliteter`. */
export function avvisBestilling(b, av, atMs, grunn) {
    return { ...b, status: 'avvist', behandletAv: av, behandletMs: atMs, avvistGrunn: grunn.slice(0, 500) };
}
/** Marker en bestilling som utplassert, koblet til den resulterende kapabiliteten. */
export function utplasserBestilling(b, kapabilitetId, av, atMs) {
    return { ...b, status: 'utplassert', kapabilitetId, behandletAv: av, behandletMs: atMs };
}
//# sourceMappingURL=kapabilitet.js.map